import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { SidebarHeader } from "@/components/sidebar/components/SidebarHeader.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useMenuCounters } from "@/queries/settings/use-app.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { FontAwesomeIcon, SidebarPanelItem, Stack, Title } from "@skailer-com/uikit";
import { ComponentProps, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface SideBarSurveysItem {
  titleGroup?: string;
  children: ComponentProps<typeof SidebarPanelItem>[];
}

export const SidebarSurveys = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { canIEdit, canIView, isFeatureEnabled } = usePermissions();
  const { data: menuCounters } = useMenuCounters();

  const isSurveysEnabled =
    isFeatureEnabled(ApiFeatureType.talent_surveys) && canIEdit(ApiPermission.surveys_module);

  const canViewSurveyTemplates =
    isSurveysEnabled &&
    canIEdit(ApiPermission.surveys_module) &&
    canIView(ApiPermission.survey_templates);

  const requestsCount = menuCounters?.survey?.requests.count ?? 0;

  const isManageSurveysActive =
    (pathname === "/surveys" || pathname.startsWith("/surveys/")) &&
    !pathname.startsWith("/surveys/requests") &&
    !pathname.startsWith("/surveys/history");

  const menu: SideBarSurveysItem[] = [
    {
      children: [
        {
          title: t("common:requests"),
          to: "/surveys/requests",
          isActive: pathname === "/surveys/requests" || pathname.startsWith("/surveys/requests"),
          icon: <FontAwesomeIcon icon="fa-light fa-inbox" />,
          subtitleSlot:
            requestsCount > 0 ? <NotificationCount count={requestsCount} size={"sm"} /> : undefined,
        },
      ],
    },
    {
      titleGroup: t("surveys:manage"),
      children: [
        {
          title: t("surveys:surveys"),
          to: "/surveys",
          isActive: isManageSurveysActive,
        },
        ...(canViewSurveyTemplates
          ? [
              {
                title: t("common:templates"),
                to: "/templates/surveys",
                isActive: pathname.startsWith("/templates/surveys"),
              },
            ]
          : []),
      ],
    },
    {
      titleGroup: t("surveys:my_surveys"),
      children: [
        {
          title: t("surveys:survey_history"),
          to: "/surveys/history",
          isActive: pathname === "/surveys/history" || pathname.startsWith("/surveys/history"),
        },
      ],
    },
  ];

  return (
    <>
      <SidebarHeader title={t("surveys:surveys")} />
      <Stack className={"px-4"} gap={"xs"}>
        {menu.map(({ children, titleGroup }, index) => (
          <Fragment key={`surveys-group-${index}`}>
            {titleGroup && (
              <Title header={titleGroup} variant="secondary" size="xs" className="ml-2 mt-4" />
            )}
            {children.map((item) => (
              <SidebarPanelItem
                key={item.to}
                to={item.to}
                isActive={item.isActive}
                title={item.title}
                icon={item.icon}
                count={item.count}
                subtitleSlot={item.subtitleSlot}
              />
            ))}
          </Fragment>
        ))}
      </Stack>
    </>
  );
};
